import React, { memo, useCallback, useMemo, useState } from "react";
import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import { ViewTableMeta } from "@hooks/view/useViewTableMeta";
import { Amount } from "@design-system/Amount/Amount";
import { NumberFormat } from "@design-system/NumberFormat/NumberFormat";
import {
    MatchableItem,
    MatchItemType,
    AccrualMatchTableCallbacks,
} from "../types";
import { AllocationTableOptions } from "../editAllocation/AllocationTable";
import styles from "../EditAllocationDrawer.module.scss";

type AllocationMeta = ViewTableMeta<
    AccrualMatchTableCallbacks,
    AllocationTableOptions
>;

export const PaymentCell = memo(
    ({
        row,
        table,
    }: CellContext<ViewTableFeatures, MatchableItem, unknown>) => {
        const meta = table.options.meta as AllocationMeta | undefined;
        const section = meta?.options?.section;
        const paymentAmounts = meta?.options?.paymentAmounts;
        const onPaymentChange = meta?.callbacks?.onPaymentChange;
        const onRemainingAmountChange =
            meta?.callbacks?.onRemainingAmountChange;
        const item = row.original;
        const isLineItem = row.depth > 0;

        if (section === "source") {
            return <ViewTableCell />;
        }

        if (section === "remaining") {
            return (
                <ViewTableCell captureClicks>
                    <RemainingAmountInput
                        id={item.id}
                        amount={item.amount}
                        maxAmount={
                            meta?.options?.amountToBeAllocated ?? Infinity
                        }
                        onChange={onRemainingAmountChange}
                    />
                </ViewTableCell>
            );
        }

        if (!isLineItem) {
            if (item.type === MatchItemType.TRANSACTION) {
                return (
                    <ViewTableCell>
                        <Amount amount={Math.abs(item.amount)} />
                    </ViewTableCell>
                );
            }

            const lineItems = item.lineItems ?? [];
            const total = lineItems.reduce(
                (sum, li) => sum + (paymentAmounts?.[li.id] ?? 0),
                0,
            );
            return (
                <ViewTableCell>
                    <Amount amount={total} />
                </ViewTableCell>
            );
        }

        const openAmount = item.openAmount ?? 0;

        if (openAmount === 0) {
            return (
                <ViewTableCell>
                    <span className={styles.zeroOpenDash}>-</span>
                </ViewTableCell>
            );
        }

        const paymentAmount = paymentAmounts?.[item.id] ?? 0;

        return (
            <ViewTableCell captureClicks>
                <PaymentInput
                    lineItemId={item.id}
                    openAmount={openAmount}
                    paymentAmount={paymentAmount}
                    onPaymentChange={onPaymentChange}
                />
            </ViewTableCell>
        );
    },
);
PaymentCell.displayName = "PaymentCell";

const PaymentInput = memo(
    ({
        lineItemId,
        openAmount,
        paymentAmount,
        onPaymentChange,
    }: {
        readonly lineItemId: string;
        readonly openAmount: number;
        readonly paymentAmount: number;
        readonly onPaymentChange?: (
            id: string,
            value: number,
            max: number,
        ) => void;
    }) => {
        const [localValue, setLocalValue] = useState<number | undefined>(
            paymentAmount,
        );
        const [prevPaymentAmount, setPrevPaymentAmount] =
            useState(paymentAmount);
        if (!Object.is(paymentAmount, prevPaymentAmount)) {
            setPrevPaymentAmount(paymentAmount);
            setLocalValue(paymentAmount);
        }

        const handleValueChange = useCallback(
            (values: { floatValue: number | undefined }) => {
                setLocalValue(values.floatValue);
            },
            [],
        );

        const handleBlur = useCallback(() => {
            const value = localValue ?? 0;
            onPaymentChange?.(lineItemId, value, openAmount);
        }, [localValue, lineItemId, openAmount, onPaymentChange]);

        const isOverMax = useMemo(
            () => typeof localValue === "number" && localValue > openAmount,
            [localValue, openAmount],
        );

        const inputClassName = isOverMax
            ? `${styles.paymentInput} ${styles.paymentInputError}`
            : styles.paymentInput;

        return (
            <NumberFormat
                value={localValue}
                onValueChange={handleValueChange}
                prefix="$ "
                decimalScale={2}
                fixedDecimalScale
                allowEmptyFormatting
                thousandSeparator
                allowNegative={false}
                className={inputClassName}
                onBlur={handleBlur}
            />
        );
    },
);
PaymentInput.displayName = "PaymentInput";

const RemainingAmountInput = memo(
    ({
        id,
        amount,
        maxAmount,
        onChange,
    }: {
        readonly id: string;
        readonly amount: number;
        readonly maxAmount: number;
        readonly onChange?: (id: string, amount: number) => void;
    }) => {
        const [localValue, setLocalValue] = useState<number | undefined>(
            amount,
        );
        const [prevAmount, setPrevAmount] = useState(amount);
        if (!Object.is(amount, prevAmount)) {
            setPrevAmount(amount);
            setLocalValue(amount);
        }

        const handleValueChange = useCallback(
            (values: { floatValue: number | undefined }) => {
                setLocalValue(values.floatValue);
            },
            [],
        );

        const handleBlur = useCallback(() => {
            const value = Math.max(0, localValue ?? 0);
            onChange?.(id, value);
        }, [localValue, id, onChange]);

        const isOverMax = useMemo(
            () => typeof localValue === "number" && localValue > maxAmount,
            [localValue, maxAmount],
        );

        const inputClassName = isOverMax
            ? `${styles.paymentInput} ${styles.paymentInputError}`
            : styles.paymentInput;

        return (
            <NumberFormat
                value={localValue}
                onValueChange={handleValueChange}
                prefix="$ "
                decimalScale={2}
                fixedDecimalScale
                allowEmptyFormatting
                thousandSeparator
                allowNegative={false}
                className={inputClassName}
                onBlur={handleBlur}
            />
        );
    },
);
RemainingAmountInput.displayName = "RemainingAmountInput";
